
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { SharedModule } from '../@commons/ui-components/shared.module';
import { ArticleComponent } from '../@commons/@components/article/article.component';
import { HeaderHomeComponent } from './@components/header-home/header-home.component';
import { fadeIn } from '../@commons/animations/animations';
import { Router } from '@angular/router';
import routesConstants from '../@commons/constants/routes.constants';
import { GardenTodayCardComponent } from './@components/garden-today-card/garden-today-card.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StorageService } from '../@commons/@services/front/storage.service';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { User } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/user.entity';
import { CultivationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-api';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import { AddressToUserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address-to-user/address-to-user-api';
import { UserResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/user/user.response';
import { AddressToUserListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/address-to-user-list.response';
import { PagingResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/pagination/paging.response';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { CultivationHierarchyApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-hierarchy/cultivation-hierarchy-api';
import { MyCultivationHierarchyListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/my-cultivation-hierarchy-list.response';
import { PlantsAttentionCarouselComponent } from './@components/plants-attention-carousel/plants-attention-carousel.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { ArticleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/article/article-api';
import {
  ArticleListRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/article/article-list.request';
import { FeedbackModalComponent } from '../@commons/@modals/feedback-modal/feedback-modal.component';
import { MessageCircleQuestionIconComponent } from '../@icons/message-circle-question-icon.component';
import { HexToHsl } from '../@commons/pipes/HexToHsl';
import { ChevronRightIconComponent } from '../@icons/chevron-right.component';
import { AlertsBannerComponent } from './@components/alerts-banner/alerts-banner.component';
import { PosthogService } from '../@commons/@services/front/posthog.service';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { GddMilestonesWidgetComponent, GddMilestoneItem } from './@components/gdd-milestones-widget/gdd-milestones-widget.component';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { GardenAdvicesWidgetComponent } from './@components/garden-advices-widget/garden-advices-widget.component';
import { PremiumTeaserComponent } from '../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../@commons/@toasts/premium-toast/premium-toast.component';
import { PremiumService } from '../@commons/@services/front/premium.service';

/**
 * Ce que l'accueil charge, et pourquoi si peu.
 *
 * L'accueil est un briefing : il montre le dessus de la pile et renvoie aux
 * ecrans specialises pour le reste. Charger vingt articles pour en lire deux,
 * ou cinquante plantes pour en resumer trois, se paie a chaque ouverture.
 */
const HOME_ARTICLES = 3;
const HOME_GDD_ITEMS = 3;

@Component({
  selector: 'vw-home', // Indicates that this component is standalone
  imports: [
    SharedModule,
    GardenTodayCardComponent,
    ArticleComponent,
    HeaderHomeComponent,
    TranslatePipe,
    HeaderPaddingSafeAreaComponent,
    PlantsAttentionCarouselComponent,
    SkeletonLoaderComponent,
    FeedbackModalComponent,
    MessageCircleQuestionIconComponent,
    HexToHsl,
    ChevronRightIconComponent,
    AlertsBannerComponent,
    GddMilestonesWidgetComponent,
    GardenAdvicesWidgetComponent,
    PremiumTeaserComponent,
    PremiumToastComponent
], // Imports shared and common modules
  templateUrl: './home.component.html', // Path to the component's template
  styleUrls: ['./home.component.scss'], // Path to the component's styles
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: ArticleApi,
      useClass: ArticleApi,
    },
    {
      provide: UserApi,
      useClass: UserApi,
    },
    {
      provide: CultivationApi,
      useClass: CultivationApi,
    },
    {
      provide: CultivationHierarchyApi,
      useClass: CultivationHierarchyApi,
    },
    {
      provide: WeatherApi,
      useClass: WeatherApi,
    },
    {
      provide: AddressToUserApi,
      useClass: AddressToUserApi,
    },
    {
      provide: PlantApi,
      useClass: PlantApi,
    },
    {
      provide: AdviceApi,
      useClass: AdviceApi,
    },
  ]
})
export class HomeComponent implements OnInit {
  isModalOpen: boolean = false; // State variable to manage modal visibility
  hierarchies: MyCultivationHierarchyListResponse[] = []; // Array to hold the list of plants
  articles: any[] = []; // Array to hold badge information
  gddMilestones: GddMilestoneItem[] = []; // In-progress plants with GDD/thermal status
  advice: AdviceTodayResponse | null = null; // Today's triggers + advice cards (one fetch for card + widget)
  user: User;
  userAddressUid: string;
  userLoaded: boolean = false;
  isLoading: boolean = true;
  isFeedbackModalOpen: boolean = false;
  readonly gddMaxItems = HOME_GDD_ITEMS;

  /**
   * Un invité n'a ni jardin ni conseils : la route lui est fermée côté serveur.
   *
   * On lit le jeton, comme le fait déjà le chatbot — c'est instantané et
   * gratuit. Appeler quand même produisait un 403 à chaque ouverture, dans le
   * journal d'incidents de production : une porte fermée dont on connaissait
   * déjà le verrou.
   */
  private readonly authApi = new AuthApi();

  readonly premiumService = inject(PremiumService);
  premiumPaywallMode: boolean = false;
  /** D'où vient l'ouverture du paywall, pour savoir quel encart convertit. */
  paywallSource: string = 'home';

  /**
   * Les encarts n'apparaissent que sur une surface où la valeur vient d'être
   * DÉMONTRÉE : sous les conseils du jour, sous la progression des récoltes.
   * Jamais dans le vide, jamais en interstitiel, et jamais pour un abonné.
   */
  get showTeasers(): boolean {
    // `ready()` est indispensable : avant l'hydratation du jeton, `isPremium()`
    // vaut `false` par DÉFAUT et non par constat. Sans ce garde-fou, un abonné
    // voit l'offre payante clignoter à chaque ouverture de l'app. Premium
    // fermé, `hasPremiumAccess()` est vrai pour tous : aucun encart.
    return this.premiumService.ready() && !this.premiumService.hasPremiumAccess();
  }

  openPaywall(source: string): void {
    this.paywallSource = source;
    this.premiumPaywallMode = true;
  }


  constructor(
    private router: Router,
    private userApi: UserApi,
    private addressToUserApi: AddressToUserApi,
    private cultivationApi: CultivationApi,
    private cultivationHierarchyApi: CultivationHierarchyApi,
    private storageService: StorageService,
    private translate: TranslateService,
    private weatherApi: WeatherApi,
    private articleApi: ArticleApi,
    private posthogService: PosthogService,
    private plantApi: PlantApi,
    private adviceApi: AdviceApi,
  ) { }

  async ngOnInit() {
    try {
      await this.loadUser();
      this.loadGddMilestones(); // fire-and-forget: needs the address (set by loadUser), must not block home render
      this.loadAdvice(); // fire-and-forget: feeds the weather card chips + the advices widget
      await this.loadUserCultivations();
      const articles = await this.articleApi.list(new ArticleListRequest(), 1, HOME_ARTICLES, this.storageService.getCurrentLanguage());
      this.articles = articles.results;
    } catch (e) {
      console.error('HomeComponent: failed to initialize', e);
    } finally {
      this.isLoading = false;
    }
  }

  async loadUser() {
    const userResponse: UserResponse = await this.userApi.getCurrentUser();
    this.user = userResponse;
    const addressToUserPaging: PagingResponse<AddressToUserListResponse> =
      await this.addressToUserApi.list(
        {
          userUid: userResponse.uid,
        },
        0,
        10,
        this.storageService.getCurrentLanguage()
      );
    if (addressToUserPaging.results.length > 0) {
      this.userAddressUid = addressToUserPaging.results[0].addressUid;
      this.userApi.addUserAddressUid(this.userAddressUid);
    }

    if (this.user && this.user.preferredLanguage) {
      this.storageService.setCurrentLang(
        this.user.preferredLanguage.toUpperCase()
      );
      this.translate.use(this.user.preferredLanguage.toLowerCase());
    }
    this.userLoaded = true;
    this.posthogService.identify(userResponse.uid, {
      language: this.user.preferredLanguage,
    });
  }

  /** Single fetch shared by the weather card (chips/lunar) and the advices widget. */
  async loadAdvice() {
    if (this.authApi.isGuest()) {
      return;
    }
    try {
      this.advice = await this.adviceApi.getToday(this.storageService.getCurrentLanguage());
    } catch (e) {
      console.warn('HomeComponent: failed to load garden advice', e);
    }
  }

  async loadUserCultivations() {
    this.hierarchies = await this.cultivationHierarchyApi.myGarden(
      this.storageService.getCurrentLanguage()
    );
  }

  /**
   * Loads the user's in-progress plants and maps them to GDD milestone items.
   * Best-effort: a failure must not break the home page (widget self-hides when empty).
   */
  async loadGddMilestones() {
    try {
      // L'endpoint dédié plutôt que la liste des plantes : celle-ci ramenait
      // dix plantes entières — variété, étape, accumulation — pour dessiner
      // trois barres, et surtout elle ne portait PAS la date de récolte
      // estimée, qui n'atteignait donc jamais l'écran.
      const batch = await this.plantApi.getThermalProgressBatch();
      this.gddMilestones = (batch?.items ?? [])
        // Une plante suffit à figurer au suivi dès qu'elle a une étape en
        // cours : filtrer sur l'accumulation thermique vidait la section pour
        // tout un pan du jardin, celui qui n'en a pas.
        .filter((item) => !!item.currentStepName)
        .map((item): GddMilestoneItem => ({
          plantUid: item.plantUid,
          cultivationUid: item.cultivationUid,
          variantUid: item.variantUid,
          cultivationName: this.resolveLabel(item.cultivationName),
          variantName: this.resolveLabel(item.variantName),
          locationName: item.locationName ?? '',
          currentStepName: this.resolveLabel(item.currentStepName),
          // Le degré-jour quand il existe, la durée de l'étape sinon.
          progress: item.thermalProgress?.progress ?? item.stepProgress ?? 0,
          // Nul pour un compte gratuit : le serveur retire la date, l'app
          // n'a rien à masquer.
          estimatedDaysLeft: item.thermalProgress?.estimatedDaysLeft ?? null,
          conditionBadge: item.thermalProgress?.conditionBadge ?? 'NORMAL',
        }));
    } catch (e) {
      console.error('HomeComponent: failed to load GDD milestones', e);
    }
  }

  /** Resolves a backend label (already a string when a lang is passed, else a LabelDto) to a display string. */
  private resolveLabel(label: any): string {
    if (!label) return '';
    return typeof label === 'string' ? label : (label.fr ?? '');
  }

  /**
   * Emits a profile click event to the parent component.
   */
  onProfileClick() {
    this.router.navigate([routesConstants.PROFILE]);
  }

  /**
   * Opens the modal by setting isModalOpen to true.
   */
  openModal() {
    this.isModalOpen = true; // Open the modal
  }

  /**
   * Closes the modal by setting isModalOpen to false.
   */
  closeModal() {
    this.isModalOpen = false; // Close the modal
  }

  /**
   * Logs a message when the modal is closed and invokes closeModal.
   */
  onModalClose() {
    this.closeModal(); // Close the modal
  }

  /**
   * Logs a confirmation message and closes the modal.
   */
  confirmAction() {
    this.closeModal(); // Close the modal
  }

  onFeedbackClicked() {
    this.router.navigate([routesConstants.FEEDBACK]);
  }

  onNotificationsClicked() {
    this.router.navigate([routesConstants.NOTIFICATIONS]);
  }

  onAllArticles() {
    this.router.navigate([routesConstants.ARTICLES]);
  }

  onAddPlant() {
    this.router.navigate([routesConstants.GARDEN_ADDPLANT]);
  }
}
